import { AssetFolderDoc } from '@bb/domain/dist/schemas';
import { Color } from '@bb/elements-web';
import { Card, Icon, List, ListItem, ListItemMeta } from 'components';
import React from 'react';
import styled from 'styled-components';

type AssetsFolderListProps = {
  actionsEnabled?: boolean;
  assets: AssetFolderDoc[];
  currPage: number;
  dataCy?: string;
  folderId?: string;
  loading?: boolean;
  onFolderClick: (folder: AssetFolderDoc) => void;
  onPageChange: (page: number) => void;
  onSettingsClick: (folder: AssetFolderDoc) => void;
  pageSize: number;
  title: string;
  totalAssets: number;
};

const AssetsFolderList: React.FC<AssetsFolderListProps> = ({
  actionsEnabled = true,
  assets,
  currPage,
  dataCy,
  folderId,
  loading = false,
  onFolderClick,
  onPageChange,
  onSettingsClick,
  pageSize = 6,
  title,
  totalAssets,
}) => (
  <StyledCard title={title}>
    <List
      data-cy={dataCy}
      size="small"
      split={false}
      loading={loading}
      itemLayout="horizontal"
      dataSource={assets}
      renderItem={(item: AssetFolderDoc) => (
        <StyledListItem
          $isSelected={folderId === item.id}
          data-cy={`${dataCy}-item-${item.id}`}
          onClick={() => onFolderClick(item)}
          actions={
            actionsEnabled && [
              <span
                key={`${item.id}-settings-icon`}
                data-cy="settings-icon-button"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onSettingsClick(item);
                }}
              >
                <Icon type="antdSettingOutlined" />
              </span>,
            ]
          }
        >
          <ListItemMeta
            avatar={<Icon type="antdFolderFilled" width={14} />}
            description={item.name}
          />
        </StyledListItem>
      )}
      pagination={{
        pageSize,
        total: totalAssets,
        current: currPage,
        onChange: onPageChange,
        showLessItems: true,
        showSizeChanger: false,
      }}
    />
  </StyledCard>
);

export default AssetsFolderList;

const StyledCard = styled(Card)`
  && {
    padding: 0;
  }

  && {
    .ant-card-head {
      padding: 5px 10px;
    }
    .ant-card-head-title {
      font-size: 18px;
      color: ${Color.PLACEHOLDER};
      padding: 10px 0;
    }
    .ant-card-body {
      padding: 5px 5px;
    }
  }
`;

const StyledListItem = styled(ListItem)<{ $isSelected: boolean }>`
  cursor: pointer;
  background-color: ${({ $isSelected }) =>
    $isSelected ? `${Color.BEERUS}66` : 'transparent'};
  border: 1px solid transparent;
  border-color: ${({ $isSelected }) =>
    $isSelected ? Color.BEERUS : 'transparent'};

  && .ant-list-item-action span[data-cy='settings-icon-button'] {
    opacity: 0;
    transition: opacity 0s;
    pointer-events: none;
  }

  &:hover {
    font-weight: 500;
    color: ${Color.PRIMARY_TEXT};

    && .ant-list-item-action span[data-cy='settings-icon-button'] {
      opacity: 1;
      pointer-events: auto;
    }
  }

  font-weight: ${({ $isSelected }) => ($isSelected ? '500' : '400')};
  color: ${({ $isSelected }) => ($isSelected ? Color.PRIMARY_TEXT : 'inherit')};
`;
